import {
  REQUEST_GROUPED_BY_SUPPLIER,
  REQUEST_FILTER,
  REQUEST_ADD,
  REQUEST_UPDATE,
  REQUEST_DELETE,
  REQUEST_APPROVE,
  REQUEST_STATUS,
  REQUEST_RECEIVED,
  REQUEST_PO,
  REQUEST_APPLY_QUOTATION,
  REQUEST_TYPES,
  REQUEST_PROJECTS,
  REQUEST_MAX_VALUES,
  REQUEST_LOG_ADD,
  REQUEST_LOG_BY_REQUEST,
  PO_LAST,
  PO_ADD,
  USER_NAMES,
} from "../../../api/ApiEndpoints";
import api from "../../../interceptor/Interceptor";
import {
  fetchRequestsStart,
  fetchRequestsSuccess,
  fetchRequestsFailure,
  addRequestStart,
  addRequestSuccess,
  addRequestFailure,
  editRequestStart,
  editRequestSuccess,
  editRequestFailure,
  deleteRequestsStart,
  deleteRequestsSuccess,
  deleteRequestsFailure,
  updateStatusStart,
  updateStatusSuccess,
  updateStatusFailure,
  fetchLogsStart,
  fetchLogsSuccess,
  fetchLogsFailure,
  addLogStart,
  addLogSuccess,
  addLogFailure,
  fetchPoNumStart,
  fetchPoNumSuccess,
  fetchPoNumFailure,
  fetchLookupsStart,
  fetchLookupsSuccess,
  fetchLookupsFailure,
} from "../../reducer/requestsReducer/RequestReducer";

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

// .................... List / Filter ...........................

export const fetchRequests = (query, page = 1, pageSize = 10, focusId = null) => async (dispatch) => {
  dispatch(fetchRequestsStart());
  try {
    const base =
      query && Object.keys(query).length > 0
        ? `${REQUEST_FILTER}?query=${encodeURIComponent(JSON.stringify(query))}&`
        : `${REQUEST_GROUPED_BY_SUPPLIER}?`;
    const url = `${base}page=${page}&pageSize=${pageSize}${focusId ? `&focus=${focusId}` : ""}`;
    const response = await api.get(url);
    const bySupplier = response.data.suppliers || {};
    const pagination = response.data.pagination || {
      page, pageSize, totalItems: 0, totalPages: 1, totalRequests: 0, totalSuppliers: 0,
    };
    const supplierOrder = response.data.supplierOrder || Object.keys(bySupplier);
    const supplierTotals = response.data.supplierTotals || {};
    const approvedBySupplier = response.data.approvedBySupplier || {};
    dispatch(fetchRequestsSuccess({ bySupplier, supplierOrder, supplierTotals, approvedBySupplier, pagination }));
    return { success: true, pagination };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch requests");
    dispatch(fetchRequestsFailure(message));
    return { success: false, error: message };
  }
};

// .................... Action log ...........................

export const addActionLog = (requestId, by, type, content) => async (dispatch) => {
  dispatch(addLogStart());
  try {
    await api.post(REQUEST_LOG_ADD, { requestId, by, type, content });
    dispatch(addLogSuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to add action log");
    dispatch(addLogFailure(message));
    return { success: false, error: message };
  }
};

export const fetchRequestLogs = (requestId) => async (dispatch) => {
  dispatch(fetchLogsStart());
  try {
    const response = await api.get(
      `${REQUEST_LOG_BY_REQUEST}?query=${encodeURIComponent(requestId)}`,
    );
    dispatch(fetchLogsSuccess({ requestId, logs: response.data.logs || [] }));
    return { success: true, logs: response.data.logs || [] };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch logs");
    dispatch(fetchLogsFailure(message));
    return { success: false, error: message };
  }
};

// .................... Add / Edit / Delete ...........................

export const addRequest = (newItem, by) => async (dispatch) => {
  dispatch(addRequestStart());
  try {
    const response = await api.post(REQUEST_ADD, { by, ...newItem });
    dispatch(addRequestSuccess());
    await dispatch(addActionLog(response.data.id, by, "Request Added"));
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to add request");
    dispatch(addRequestFailure(message));
    return { success: false, error: message };
  }
};

export const editRequest = (id, updatedItem, by) => async (dispatch) => {
  dispatch(editRequestStart());
  try {
    await api.put(`${REQUEST_UPDATE}${id}`, updatedItem);
    dispatch(editRequestSuccess());
    await dispatch(addActionLog(id, by, "Request Edited"));
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to update request");
    dispatch(editRequestFailure(message));
    return { success: false, error: message };
  }
};

export const deleteRequests = (ids) => async (dispatch) => {
  dispatch(deleteRequestsStart());
  try {
    await api.post(REQUEST_DELETE, { ids });
    dispatch(deleteRequestsSuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to delete request(s)");
    dispatch(deleteRequestsFailure(message));
    return { success: false, error: message };
  }
};

// .................... Status flow ...........................

export const approveRequest = (id, by, undo = false) => async (dispatch) => {
  dispatch(updateStatusStart());
  try {
    const newStatus = undo ? "new" : "approved";
    await api.put(`${REQUEST_APPROVE}${id}`, { newStatus });
    dispatch(updateStatusSuccess());
    await dispatch(
      addActionLog(id, by, undo ? "Request Approval Withdrawn" : "Request Approved"),
    );
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      undo ? "Failed to undo approve request" : "Failed to approve request",
    );
    dispatch(updateStatusFailure(message));
    return { success: false, error: message };
  }
};

export const sendOrder = (id, by, undo = false) => async (dispatch) => {
  dispatch(updateStatusStart());
  const newStatus = undo ? "poMade" : "ordered";
  try {
    await api.put(`${REQUEST_STATUS}${id}`, { newStatus });
    dispatch(updateStatusSuccess());
    await dispatch(
      addActionLog(id, by, undo ? "Order Has Not Been Placed" : "Order Placed"),
    );
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, `Failed to update request status to ${newStatus}`);
    dispatch(updateStatusFailure(message));
    return { success: false, error: message };
  }
};

export const receiveRequest = (payload) => async (dispatch) => {
  dispatch(updateStatusStart());
  try {
    await api.post(REQUEST_RECEIVED, payload);
    dispatch(updateStatusSuccess());
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to set request status to received");
    dispatch(updateStatusFailure(message));
    return { success: false, error: message };
  }
};

// .................... Purchase order number ...........................

export const fetchLastPoNum = () => async (dispatch) => {
  dispatch(fetchPoNumStart());
  try {
    const response = await api.get(PO_LAST);
    dispatch(fetchPoNumSuccess(response.data.poNum || 0));
    return { success: true, poNum: response.data.poNum || 0 };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch last used PO Number");
    dispatch(fetchPoNumFailure(message));
    return { success: false, error: message };
  }
};

export const updateLastPoNum = (newPoNum) => async (dispatch) => {
  dispatch(fetchPoNumStart());
  try {
    await api.put(PO_LAST, { poNum: newPoNum });
    dispatch(fetchPoNumSuccess(newPoNum));
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to update last PO Number");
    dispatch(fetchPoNumFailure(message));
    return { success: false, error: message };
  }
};

export const incrementPoNum = () => async (dispatch) => {
  try {
    const response = await api.post(PO_ADD);
    dispatch(fetchPoNumSuccess(response.data.poNum));
    return { success: true, poNum: response.data.poNum };
  } catch (error) {
    const message = extractErrorMessage(error, "PO # was not incremented");
    return { success: false, error: message };
  }
};

// .................... PO generation / quotations ...........................

export const generatePO = (ids, poNum, project, poDocName) => async (dispatch) => {
  try {
    await api.post(REQUEST_PO, { ids, poNum, project, poDocName });
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to update PO # in requests");
    return { success: false, error: message };
  }
};

export const applyQuotation = (itemIds, quotationDocName) => async (dispatch) => {
  try {
    await api.post(REQUEST_APPLY_QUOTATION, { itemIds, quotationDocName });
    return { success: true };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to apply quotation to requests");
    return { success: false, error: message };
  }
};

// .................... Lookups (types / projects / max values / user names) .....

export const fetchLookups = () => async (dispatch) => {
  dispatch(fetchLookupsStart());
  try {
    const [typesRes, projectsRes, maxValuesRes, userNamesRes] = await Promise.all([
      api.get(REQUEST_TYPES),
      api.get(REQUEST_PROJECTS),
      api.get(REQUEST_MAX_VALUES),
      api.get(USER_NAMES),
    ]);
    const payload = {
      types: typesRes.data.types || [],
      projects: projectsRes.data.projects || [],
      maxValues: {
        unitSize: maxValuesRes.data.maxUnitSize || 0,
        numUnits: maxValuesRes.data.maxNumUnits || 0,
        unitPrice: maxValuesRes.data.maxUnitPrice || 0,
      },
      userNames: userNamesRes.data.users || [],
    };
    dispatch(fetchLookupsSuccess(payload));
    return { success: true, ...payload };
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to fetch filter lookups");
    dispatch(fetchLookupsFailure(message));
    return { success: false, error: message };
  }
};